import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useCan } from "../auth/AuthProvider";
import { AFFILIATE_URL } from "../lib/affiliate";
import { useApps, useDatabases, useRegisteredDomains, useTunnels } from "../lib/queries";
import { rankCommands, scoreCommand, type Rankable } from "../lib/palette";
import { describeEngine } from "../lib/engines";
import { describeSource } from "../lib/format";
import { regionLabel } from "../lib/regions";
import { Icon, type IconName } from "./icons";
import { cx } from "./ui";

/*
 * Hand-rolled rather than pulling `cmdk`, for the same reason the icons are
 * inlined: a dozen behaviours do not justify a dependency, and the ones that
 * matter here — ranking and keyboard order — are the ones we would end up
 * overriding anyway.
 *
 * Deliberately navigation-only. Palettes that also mutate things turn a
 * mistyped Enter into a deploy, and every destructive action in this dashboard
 * already asks for confirmation somewhere it can explain itself.
 */

/**
 * How many of each list to pull in for matching.
 *
 * 100 is the API's ceiling — `listQuerySchema` caps every list there, and
 * asking for 200 had each request refused with a 400 the palette swallowed,
 * so it matched nothing while looking like an organization with no apps. An
 * organization past a hundred of anything finds the rest from the list page.
 */
const PALETTE_LIMIT = 100;

interface Command extends Rankable {
  id: string;
  to: string;
  icon: IconName;
}

interface Section {
  /** React's key, and the name the heading is looked up under. */
  id: string;
  label: string;
  items: Command[];
}

const IS_MAC =
  typeof navigator !== "undefined" && /mac|iphone|ipad/i.test(navigator.platform || navigator.userAgent);

export function paletteShortcutLabel(): string {
  return IS_MAC ? "⌘K" : "Ctrl K";
}

/** True for the chord that should open the palette, on either platform. */
export function isPaletteChord(event: KeyboardEvent): boolean {
  return event.key.toLowerCase() === "k" && (IS_MAC ? event.metaKey : event.ctrlKey);
}

export function CommandPalette({
  orgId,
  open,
  onClose,
}: {
  orgId: string | undefined;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation("palette");
  const navigate = useNavigate();
  const canCreate = useCan("developer");
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  /* Passing `undefined` for the org leaves both queries disabled, so nothing is
     fetched until the palette is first opened. After that TanStack keeps them,
     and the list is warm on every subsequent open. */
  const scopedOrg = open ? orgId : undefined;
  const apps = useApps(scopedOrg, PALETTE_LIMIT);
  const databases = useDatabases(scopedOrg, PALETTE_LIMIT);
  /*
   * Added when tunnels gained a page of their own. Before that a match here
   * had nowhere to land — the list was the only screen, and jumping to it by
   * name would have been the same as jumping to it by any other means.
   *
   * This list resolves state against the deployment engine, which the apps one
   * above does too — so opening the palette is not free. It is one engine call
   * per organization and server rather than one per connection, which is what
   * makes paying it to match names defensible.
   */
  const tunnels = useTunnels(scopedOrg, { limit: PALETTE_LIMIT, offset: 0 });
  // One cheap read, no engine behind it: a domain is a row.
  const domains = useRegisteredDomains(scopedOrg, { limit: PALETTE_LIMIT, offset: 0 });

  const sections = useMemo<Section[]>(() => {
    const appItems: Command[] = (apps.data?.data ?? []).map((app) => ({
      id: `app:${app.id}`,
      label: app.name,
      hint: describeSource(app.source),
      to: `/apps/${app.id}`,
      icon: "apps",
    }));

    const dbItems: Command[] = (databases.data?.data ?? []).map((database) => ({
      id: `db:${database.id}`,
      label: database.name,
      hint: describeEngine(database.engine),
      to: `/databases/${database.id}`,
      icon: "database",
    }));

    const tunnelItems: Command[] = (tunnels.data?.data ?? []).map((tunnel) => ({
      id: `tunnel:${tunnel.id}`,
      label: tunnel.name,
      hint: regionLabel(tunnel.region) ?? t("hint.tunnel"),
      to: `/tunnels/${tunnel.id}`,
      icon: "lock",
    }));

    const domainItems: Command[] = (domains.data?.data ?? []).map((domain) => ({
      id: `domain:${domain.id}`,
      label: domain.domain,
      hint: t("hint.domain"),
      to: `/domains/${domain.id}`,
      icon: "globe",
    }));

    /*
     * Route, icon and key per row; the words come from the locale file.
     *
     * The hint stays the path itself, untranslated, and that is deliberate: it
     * is the URL the row goes to, which is the same string in every language
     * and is what somebody types when they already know where they are going.
     * Matching runs over the label too, so an Indonesian reader finds Billing
     * by typing "Penagihan" and an English one by typing "Billing" — the
     * ranking reads whatever this render put in front of them.
     */
    const goTo: Command[] = (
      [
        ["apps", "/apps", "apps"],
        ["databases", "/databases", "database"],
        ["schedules", "/schedules", "clock"],
        // Missing until tunnels had somewhere to go. The comment below says why
        // that matters: this list is the flat index of every route.
        ["tunnels", "/tunnels", "lock"],
        ["domains", "/domains", "globe"],
        ...(AFFILIATE_URL ? [["referrals", "/referrals", "gift"] as const] : []),
        ["support", "/support", "support"],
        ["newTicket", "/support/new", "plus"],
        ["team", "/settings/team", "team"],
        ["integrations", "/settings/integrations", "link"],
        ["billing", "/settings/billing", "card"],
        ["limits", "/settings/limits", "shield"],
        ["apiKeys", "/settings/api-keys", "key"],
        ["security", "/settings/security", "shield"],
        /*
         * Both reachable only through a menu otherwise — Profile from the user
         * menu, Billing details from a link on Profile and on Billing. The
         * palette is the one place that lists every route flat, so a page
         * missing from it is a page somebody can only find by knowing where it
         * lives.
         */
        ["profile", "/settings/profile", "settings"],
        ["billingDetails", "/settings/billing-details", "card"],
      ] as const
    ).map(([key, to, icon]) => ({
      id: `nav:${key}`,
      label: t(`goTo.${key}`),
      hint: to,
      to,
      icon: icon as IconName,
    }));

    const create: Command[] = canCreate
      ? (
          [
            ["app", "/apps/new"],
            ["database", "/databases/new"],
            ["schedule", "/schedules/new"],
            ["tunnel", "/tunnels/new"],
            ["domain", "/domains/new"],
          ] as const
        ).map(([key, to]) => ({
          id: `new:${key}`,
          label: t(`create.${key}`),
          hint: t("hint.create"),
          to,
          icon: "plus" as IconName,
        }))
      : [];

    const built: Section[] = [
      { id: "apps", label: t("sections.apps"), items: rankCommands(query, appItems) },
      { id: "databases", label: t("sections.databases"), items: rankCommands(query, dbItems) },
      { id: "tunnels", label: t("sections.tunnels"), items: rankCommands(query, tunnelItems) },
      { id: "domains", label: t("sections.domains"), items: rankCommands(query, domainItems) },
      { id: "create", label: t("sections.create"), items: rankCommands(query, create) },
      { id: "goTo", label: t("sections.goTo"), items: rankCommands(query, goTo) },
    ].filter((section) => section.items.length > 0);

    /* With a query, whichever section holds the best match leads — typing an
       app's name should not leave "Go to" sitting above it. Empty query keeps
       the authored order, so the palette opens looking the same every time. */
    if (query.trim() !== "") {
      built.sort((a, b) => {
        const bestA = a.items[0] ? (scoreCommand(query, a.items[0]) ?? Infinity) : Infinity;
        const bestB = b.items[0] ? (scoreCommand(query, b.items[0]) ?? Infinity) : Infinity;
        return bestA - bestB;
      });
    }

    return built;
  }, [apps.data, databases.data, query, canCreate]);

  const flat = useMemo(() => sections.flatMap((section) => section.items), [sections]);

  // A new query means a new list; the old cursor position means nothing in it.
  useEffect(() => setActive(0), [query]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
      inputRef.current?.focus();
    }
  }, [open]);

  // Keep the cursor in view when it walks past the edge of the scroll area.
  useEffect(() => {
    listRef.current
      ?.querySelector('[data-active="true"]')
      ?.scrollIntoView({ block: "nearest" });
  }, [active]);

  if (!open) return null;

  const go = (to: string) => {
    onClose();
    void navigate(to);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key === "ArrowDown" || (event.key === "Tab" && !event.shiftKey)) {
      event.preventDefault();
      setActive((index) => (flat.length === 0 ? 0 : (index + 1) % flat.length));
      return;
    }
    if (event.key === "ArrowUp" || (event.key === "Tab" && event.shiftKey)) {
      event.preventDefault();
      setActive((index) => (flat.length === 0 ? 0 : (index - 1 + flat.length) % flat.length));
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const target = flat[active];
      if (target) go(target.to);
    }
  };

  const loading = apps.isPending || databases.isPending;
  let cursor = -1;

  return (
    <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label={t("title")}>
      <button
        type="button"
        aria-label={t("close")}
        onClick={onClose}
        className="absolute inset-0 bg-canvas/80 backdrop-blur-sm"
      />

      {/* Sits high rather than centred: the list grows downward, and a centred
          panel would walk the input up the screen as results arrive. */}
      {/* `dvh` and not `vh`: on iOS Safari `vh` is the viewport with the URL bar
          scrolled away, so a palette placed and sized in `vh` sits lower and
          runs longer than the screen actually showing — the same correction the
          modals already carry. */}
      <div className="absolute inset-x-4 top-[12dvh] mx-auto max-w-xl sm:inset-x-0">
        <div
          onKeyDown={onKeyDown}
          className="overflow-hidden rounded-lg border border-line bg-surface shadow-lg"
        >
          <div className="flex items-center gap-2.5 border-b border-line px-3.5">
            <Icon name="search" className="size-4 shrink-0 text-fg-subtle" />
            <input
              ref={inputRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("placeholder")}
              aria-label={t("search")}
              className="h-11 w-full bg-transparent text-sm pointer-coarse:text-base text-fg placeholder:text-fg-subtle focus:outline-none"
            />
            <kbd className="hidden shrink-0 border border-line px-1.5 py-0.5 font-mono text-[10px] text-fg-subtle sm:block">
              esc
            </kbd>
          </div>

          <div ref={listRef} className="max-h-[54dvh] overflow-y-auto py-1.5">
            {flat.length === 0 ? (
              <p className="px-3.5 py-8 text-center text-[13px] text-fg-subtle">
                {loading ? t("loading") : t("empty", { query: query.trim() })}
              </p>
            ) : (
              sections.map((section) => (
                <div key={section.id} className="pb-1">
                  <p className="micro px-3.5 pt-1.5 pb-1">{section.label}</p>
                  {section.items.map((item) => {
                    cursor += 1;
                    const isActive = cursor === active;
                    const index = cursor;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        data-active={isActive}
                        onMouseMove={() => setActive(index)}
                        onClick={() => go(item.to)}
                        className={cx(
                          "flex w-full items-center gap-2.5 px-3.5 py-1.5 text-left transition-colors duration-100",
                          isActive ? "bg-raised" : "hover:bg-raised/60",
                        )}
                      >
                        <Icon
                          name={item.icon}
                          className={cx(
                            "size-3.5 shrink-0",
                            isActive ? "text-fg" : "text-fg-subtle",
                          )}
                        />
                        <span className="min-w-0 flex-1 truncate text-[13px] text-fg">
                          {item.label}
                        </span>
                        {item.hint && (
                          <span className="hidden shrink-0 truncate font-mono text-[11px] text-fg-subtle sm:block">
                            {item.hint}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          <div className="flex items-center gap-3 border-t border-line px-3.5 py-2 text-[11px] text-fg-subtle">
            <span className="flex items-center gap-1">
              <Hint>↑</Hint>
              <Hint>↓</Hint>
              {t("footer.navigate")}
            </span>
            <span className="flex items-center gap-1">
              <Hint>↵</Hint>
              {t("footer.open")}
            </span>
            {apps.data && (
              <span className="ml-auto tabular-nums">
                {t("footer.apps", { count: apps.data.total })}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="border border-line px-1 py-px font-mono text-[10px] text-fg-subtle">
      {children}
    </kbd>
  );
}
